// API请求管理器 - 防止重复请求和资源耗尽
class RequestManager {
  constructor() {
    this.pendingRequests = new Map(); // 存储正在进行的请求
    this.requestCounts = new Map(); // 存储请求计数
    this.lastRequestTimes = new Map(); // 存储上次请求时间
    this.MAX_CONCURRENT_REQUESTS = 5; // 最大并发请求数
    this.MIN_REQUEST_INTERVAL = 1000; // 最小请求间隔(ms)
    this.REQUEST_TIMEOUT = 30000; // 请求超时时间(ms)
  }

  // 生成请求键
  generateRequestKey(url, options = {}) {
    const method = options.method || 'GET';
    const body = options.body ? JSON.stringify(options.body) : '';
    return `${method}:${url}:${body}`;
  }

  // 检查是否可以发起请求
  canMakeRequest(requestKey) {
    const now = Date.now();
    
    // 检查是否有相同的请求正在进行
    if (this.pendingRequests.has(requestKey)) {
      console.log(`请求 ${requestKey} 正在进行中，跳过重复请求`);
      return false;
    }

    // 检查并发请求数量
    if (this.pendingRequests.size >= this.MAX_CONCURRENT_REQUESTS) {
      console.log(`已达到最大并发请求数(${this.MAX_CONCURRENT_REQUESTS})，等待其他请求完成`);
      return false;
    }

    // 检查请求间隔
    const lastRequestTime = this.lastRequestTimes.get(requestKey);
    if (lastRequestTime && (now - lastRequestTime) < this.MIN_REQUEST_INTERVAL) {
      console.log(`请求 ${requestKey} 间隔过短，请等待 ${this.MIN_REQUEST_INTERVAL}ms`);
      return false;
    }

    return true;
  }

  // 发起受管理的请求
  async makeRequest(url, options = {}) {
    const requestKey = this.generateRequestKey(url, options);
    
    // 检查是否可以发起请求
    if (!this.canMakeRequest(requestKey)) {
      // 如果有相同请求正在进行，等待其完成
      if (this.pendingRequests.has(requestKey)) {
        console.log(`等待现有请求 ${requestKey} 完成...`);
        return await this.pendingRequests.get(requestKey);
      }
      
      // 其他情况直接拒绝
      throw new Error(`请求被拒绝: ${requestKey}`);
    }

    const now = Date.now();
    this.lastRequestTimes.set(requestKey, now);
    
    // 增加请求计数
    const count = (this.requestCounts.get(requestKey) || 0) + 1;
    this.requestCounts.set(requestKey, count);
    
    console.log(`发起请求 ${requestKey} (第${count}次)`);

    // 创建带超时的请求Promise
    const requestPromise = this.createTimeoutRequest(url, options);
    
    // 存储正在进行的请求
    this.pendingRequests.set(requestKey, requestPromise);

    try {
      const result = await requestPromise;
      console.log(`请求 ${requestKey} 完成`);
      return result;
    } catch (error) {
      console.error(`请求 ${requestKey} 失败:`, error.message);
      throw error;
    } finally {
      // 清理完成的请求
      this.pendingRequests.delete(requestKey);
    }
  }

  // 创建带超时的请求
  createTimeoutRequest(url, options) {
    return new Promise((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        reject(new Error(`请求超时: ${url}`));
      }, this.REQUEST_TIMEOUT);

      fetch(url, options)
        .then(response => {
          clearTimeout(timeoutId);
          resolve(response);
        })
        .catch(error => {
          clearTimeout(timeoutId);
          reject(error);
        });
    });
  }

  // 取消所有待处理的请求
  cancelAllRequests() {
    console.log(`取消 ${this.pendingRequests.size} 个待处理请求`);
    this.pendingRequests.clear();
  }

  // 获取统计信息
  getStats() {
    return {
      pendingRequests: this.pendingRequests.size,
      totalRequests: Array.from(this.requestCounts.values()).reduce((sum, count) => sum + count, 0),
      requestsByUrl: Object.fromEntries(this.requestCounts)
    };
  }

  // 重置统计信息
  resetStats() {
    this.requestCounts.clear();
    this.lastRequestTimes.clear();
    console.log('请求统计信息已重置');
  }
}

// 创建全局实例
const requestManager = new RequestManager();

export default requestManager;